// Multi-factor authentication challenge handling for in-flow (step-up) auth.
//
// The Rehive platform can return an MFA challenge mid-flow on an already
// authenticated request (e.g. creating a transaction). The error shape differs
// depending on whether the call went through the v4 SDK or the raw `callApi`
// helper, so `extractMfaChallenge` normalises both:
//
//   SDK (ApiError):   err.error.data.challenges  (err.error is the parsed body)
//   callApi envelope: err.data.challenges        (see util/rehive.js parseApi)
//
// A challenge looks like:
//   { id, type: 'authentication'|'authorization'|'setup',
//     durability: 'ephemeral'|'durable'|'permanent',
//     authenticator_types: ['totp'|'sms'|'static'], created }
//
// After verifying, the original request is retried. For `ephemeral` challenges
// the retry MUST carry a `Verified-Challenge: {id}` header (and identical
// path/method/body); for `durable`/`permanent` the verification persists
// server-side so the header is harmless.

export const VERIFIED_CHALLENGE_HEADER = 'Verified-Challenge';

export const extractMfaChallenge = err => {
  const challenges =
    err?.error?.data?.challenges ?? // SDK ApiError (parsed body on `.error`)
    err?.data?.challenges ?? // callApi { status, message, data } envelope
    err?.challenges ?? // already-unwrapped payload
    null;
  return Array.isArray(challenges) && challenges.length ? challenges[0] : null;
};

// Imperative bridge to the mounted MfaChallengeProvider.
//
// React components inside the provider use the `useMfaChallenge()` hook, but many
// privileged call sites are not positioned to use a hook — plain handlers in
// util/api.js, page-config `onSubmit`s, class components. The provider registers
// its `runWithMfa` here on mount so any code can opt a call into challenge
// handling. There is exactly one provider app-wide, so the singleton is
// unambiguous; with no provider mounted this is a transparent passthrough.
let _mfaRunner = null;

export const setMfaRunner = runner => {
  _mfaRunner = runner;
};

// runWithMfa(thunk): runs thunk(); if it throws an MFA challenge, prompts + verifies,
// then retries thunk({ headers: { 'Verified-Challenge': id } }). Non-MFA errors bubble.
export const runWithMfa = thunk =>
  _mfaRunner ? _mfaRunner(thunk) : thunk();
